import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  TextField,
  Button,
  Typography,
  Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MdClose, MdFormatListBulleted } from 'react-icons/md';
import { Capsule } from 'components/worten/modal-dialog-v2';
import { useAPITodoList } from '../hooks/useAPITodoList';
import useTasksStore from 'stores/tasksStore';
import useAuth from 'hooks/useAuth';






//-------------------------------------
const TodoList = ({ usuarioId, maxRows = 8, onClose }) => {
  const theme = useTheme();
  const { loadTodos, addTodo, toggleTodo, deleteCompleted, TodoLoadingAnimation } = useAPITodoList();
  const [newTodo, setNewTodo] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const initStartedRef = useRef(false); // Track if initialization has started
  
  const { user } = useAuth();

  // Get tasks from Zustand store
  const { getTasksForUser, initializeFromUserData, shouldFetch } = useTasksStore();
  const todos = getTasksForUser(usuarioId);

  // Pending tasks are always rendered first. When a task is marked as
  // completed it drops below the last pending one, keeping the pending work
  // on top. Array.prototype.sort is stable, so the relative order inside each
  // group is preserved.
  const orderedTodos = [...todos].sort((a, b) => {
    const aCompleted = a.estado === 'completado' ? 1 : 0;
    const bCompleted = b.estado === 'completado' ? 1 : 0;
    return aCompleted - bCompleted;
  });

  // Initialize todos from user data on mount (similar to notifications)
  useEffect(() => {
    // Only proceed if user is available and not initialized yet
    if (!user || !user.id || isInitialized || initStartedRef.current) return;

    // Mark initialization as started
    initStartedRef.current = true;

    // Decide up-front whether the store cache is stale/empty. On remount (e.g.
    // reopening the panel) the store may already hold tasks newer than the
    // login payload. Re-seeding unconditionally from `user.initData` would
    // overwrite them and drop recently added/edited tasks.
    const needsFetch = shouldFetch();

    // Seed the store from the login payload only when a refresh is required, so
    // there is instant content while the API request resolves.
    if (needsFetch) {
      initializeFromUserData(user?.initData?.todoList);
    }

    // Use the cache when it is fresh; otherwise force a fresh API fetch.
    loadTodos(usuarioId, () => {
      // Data is handled by the store, no need to set local state
    }, needsFetch);

    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Legacy useEffect for cases where usuarioId changes (kept for backward compatibility)
  useEffect(() => {
    // Only load if already initialized and usuarioId changes
    if (isInitialized && usuarioId) {
      loadTodos(usuarioId, (success, todoData) => {
        // Data is now handled by the store, no need to set local state
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioId, isInitialized]);

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewTodo('');
  };

  const handleSaveNew = () => {
    addTodo(usuarioId, newTodo, (success, payload) => {
      if (success) {
        setIsAddingNew(false);
        setNewTodo('');
        // Store is automatically updated by the API hook
      }
    });
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewTodo('');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSaveNew();
    } else if (event.key === 'Escape') {
      handleCancelNew();
    }
  };

  const handleToggleTodo = (todo) => {
    toggleTodo(todo, usuarioId, (success, payload) => {
      if (success) {
        // Store is automatically updated by the API hook
      }
    });
  };

  const handleDeleteCompleted = () => {
    deleteCompleted(usuarioId, (success, payload) => {
      if (success) {
        // Store is automatically updated by the API hook
      }
    });
  };

  // Calculate scroll height based on maxRows - more precise calculation
  const itemHeight = 48; // Standard height for ListItem with padding
  const dividerHeight = 1; // Height of divider
  const addFormHeight = 48; // Height of add new todo form when visible
  
  // Calculate total height for visible items
  const visibleItemsHeight = maxRows * (itemHeight + dividerHeight);
  const scrollHeight = isAddingNew ? visibleItemsHeight + addFormHeight : visibleItemsHeight;
  
  // Determine if scrollbar should be visible
  const totalItems = orderedTodos.length + (isAddingNew ? 1 : 0);
  const needsScroll = totalItems > maxRows;

  // Icon size derived from the header title line-box (ModalDialogV2 pattern)
  const titleIconSize = `calc(${theme.typography.h3.fontSize} * ${theme.typography.h3.lineHeight})`;

  return (
    <Box
      sx={{
        bgcolor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
      }}
    >
      <TodoLoadingAnimation />

      {/* Header (ModalDialogV2 pattern) */}
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="span"
            sx={{
              color: 'primary.light',
              fontSize: titleIconSize,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <MdFormatListBulleted />
          </Box>
          <Typography variant="h3" color="primary" fontWeight={300}>
            Tareas
          </Typography>
        </Box>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Cerrar"
            sx={{
              color: 'primary.contrastText',
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <MdClose style={{ fontSize: '2rem' }} />
          </IconButton>
        )}
      </Box>

      {/* Scrollable content area */}
      <Box
        px={1}
        pb={1}
        sx={{
          height: scrollHeight, // Use fixed height instead of maxHeight
          overflowY: needsScroll ? 'auto' : 'hidden', // Only show scroll when needed
          overflowX: 'hidden',
          minHeight: 0,
          // Scrollbar styles matching ModalDialogV2 (only when scrolling is needed)
          ...(needsScroll && {
            '&::-webkit-scrollbar': { width: 14 },
            '&::-webkit-scrollbar-track': { bgcolor: 'grey.200', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.400',
              borderRadius: 4,
              border: 1,
              borderColor: 'grey.200',
              '&:hover': { bgcolor: 'grey.600' },
            },
          }),
        }}
      >
        <Capsule sxContent={{ p: 0 }}>
        <List sx={{ p: 0 }}>
          {/* Add new todo input */}
          {isAddingNew && (
            <>
              <ListItem sx={{ 
                py: 1, 
                px: 2, 
                minHeight: addFormHeight,
                alignItems: 'center'
              }}>

                <ListItemText>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Escribe una nueva tarea..."
                    value={newTodo}
                    onChange={(e) => setNewTodo(e.target.value)}
                    onKeyDown={handleKeyPress}
                    autoFocus
                    variant="outlined"
                  />
                </ListItemText>
              </ListItem>
              <Divider />
            </>
          )}

          {/* Existing todos (pending first, completed at the bottom) */}
          {orderedTodos.map((todo, index) => (
            <React.Fragment key={todo.id}>
              <ListItem
                sx={{
                  py: 0,
                  px: 1,
                  minHeight: itemHeight,
                  alignItems: 'center',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={todo.estado === 'completado'}
                    onChange={() => handleToggleTodo(todo)}
                    color="primary"
                    size="medium"
                    sx={{
                      color: todo.estado === 'completado' ? 'primary.main' : 'text.secondary',
                      transform: 'scale(1.2)',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.5rem',
                      },
                      '&:hover': {
                        bgcolor: 'transparent'
                      },
                      '& .MuiCheckbox-root': {
                        borderRadius: '6px'
                      }
                    }}
                    icon={
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          border: '2px solid',
                          borderColor: 'text.secondary',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                    }
                    checkedIcon={
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          border: '2px solid',
                          borderColor: 'primary.main',
                          borderRadius: '4px',
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        ✔
                      </Box>
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        textDecoration: todo.estado === 'completado' ? 'line-through' : 'none',
                        color: todo.estado === 'completado' ? 'text.secondary' : 'text.primary',
                        opacity: todo.estado === 'completado' ? 0.7 : 1
                      }}
                    >
                      {todo.texto}
                    </Typography>
                  }
                />
              </ListItem>
              {index < orderedTodos.length - 1 && <Divider />}
            </React.Fragment>
          ))}

          {/* Empty state */}
          {orderedTodos.length === 0 && !isAddingNew && (
            <ListItem>
              <ListItemText>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 2 }}
                >
                  No hay tareas pendientes
                </Typography>
              </ListItemText>
            </ListItem>
          )}
        </List>
        </Capsule>
      </Box>

      {/* Footer actions (ModalDialogV2 two-column layout) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
        }}
      >
        {/* Left column - secondary action */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteCompleted}
            disabled={!todos.some(todo => todo.estado === 'completado')}
          >
            Borrar completados
          </Button>
        </Box>

        {/* Right column - primary action */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            ml: 'auto',
            minWidth: 0,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            disabled={isAddingNew}
          >
            Agregar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TodoList;
